import { useState } from 'react';

import { faShieldCheck, faTrashCan } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSelector } from 'react-redux';
import LockImg from '../../assets/img/lock.png';
import { IdentitySummaryResponseMfaTypeEnum } from '../../openapi/keymaker';
import { RootState } from '../../types';
import { formatPhoneNumber } from '../../utils/StringUtils';
import { cn } from '../../utils/classUtils';
import Button from '../Button';
import ZenCardWithItems from '../CardWithItems';
import AuthenticatorSetupForm from './AuthenticatorSetupForm';
import DeleteMFAModal from './DeleteMFAModal';
import MFAMethodSelector from './MFAMethodSelector';
import SMSSetupForm from './SMSSetupForm';
import SMSVerificationForm from './SMSVerificationForm';

interface MFASettingsProps {
  isForceMfa?: boolean;
}

const MFASettings: React.FC<MFASettingsProps> = ({ isForceMfa = false }) => {
  const { keymakerCurrentUser } = useSelector((state: RootState) => ({
    keymakerCurrentUser: state.auth.keymakerCurrentUser,
  }));

  const [isMFASetupModalOpen, setIsMFASetupModalOpen] =
    useState<boolean>(false);

  const [selectedMFAMethod, setSelectedMFAMethod] =
    useState<IdentitySummaryResponseMfaTypeEnum>();

  const [isDeleteMFAModalOpen, setIsDeleteMFAModalOpen] =
    useState<boolean>(false);

  const [verificationPhoneNumber, setVerificationPhoneNumber] =
    useState<string>();

  const isSmsTypeMFA =
    keymakerCurrentUser?.mfaType === IdentitySummaryResponseMfaTypeEnum.Sms;

  const isAuthenticatorTypeMFA =
    selectedMFAMethod === IdentitySummaryResponseMfaTypeEnum.Authenticator;

  return (
    <div className='w-full'>
      {keymakerCurrentUser?.mfaType ? (
        <ZenCardWithItems
          title='Two-Factor Authentication'
          RightComponent={
            <button
              aria-label='delete-mfa'
              className='hover:bg-red-600 hover:bg-opacity-10 cursor-pointer flex h-8 w-8 items-center justify-center rounded-full mx-3'
              onClick={() => setIsDeleteMFAModalOpen(true)}
            >
              <FontAwesomeIcon icon={faTrashCan} className='text-red-600' />
            </button>
          }
          items={[
            {
              name: 'Status',
              rightActionComponent: (
                <div className='font-medium text-success flex items-center'>
                  <FontAwesomeIcon icon={faShieldCheck} className='mr-1' />
                  <span className='mx-3'>Enabled</span>
                </div>
              ),
            },
            {
              name: 'Method',
              value: isSmsTypeMFA ? 'Text Messages (SMS)' : 'Authenticator App',
            },
            ...(isSmsTypeMFA
              ? [
                  {
                    name: 'Phone',
                    value: formatPhoneNumber(keymakerCurrentUser?.phoneNumber),
                  },
                ]
              : []),
          ]}
        />
      ) : (
        <div className='border border-gray-200'>
          <div className='flex flex-wrap justify-between items-center py-3 px-4 bg-gray-100 rounded-t-lg'>
            <p className='flex items-center'>
              <span
                className={cn(
                  'flex font-medium  items-center font-inter text-base leading-[22px] text-primary-dark',
                )}
              >
                Two-Factor Authentication
              </span>
            </p>
          </div>
          <div className='p-4 relative'>
            <p className='sticky font-inter text-dark pt-3 text-lg z-10'>
              Add a second step verification to protect your account
            </p>
            <p className='font-inter text-zen-dark-8 pt-3 text-sm'>
              Two-factor authentication (2FA) provides an additional layer of
              security beyond passwords and is strongly recommended. Once
              enabled, your account is protected by requiring both your password
              and an authentication code from an authenticator app or your
              mobile device in order to log in.
            </p>
            <img
              src={LockImg}
              alt='blocked'
              className='absolute h-28 right-0 top-0 z-0'
            />
            <p className='py-4'>
              <Button
                label='Enable Two-Factor Authentication'
                buttonSize='sm'
                onPress={() => setIsMFASetupModalOpen(true)}
              />
            </p>
          </div>
        </div>
      )}
      <MFAMethodSelector
        isOpen={isMFASetupModalOpen}
        onClose={() => setIsMFASetupModalOpen(false)}
        setSelectedMFAMethod={setSelectedMFAMethod}
      />
      <SMSSetupForm
        isOpen={selectedMFAMethod === IdentitySummaryResponseMfaTypeEnum.Sms}
        onClose={() => setSelectedMFAMethod(undefined)}
        setVerificationPhoneNumber={setVerificationPhoneNumber}
      />
      <SMSVerificationForm
        isOpen={!!verificationPhoneNumber}
        onClose={() => setVerificationPhoneNumber(undefined)}
        verificationPhoneNumber={verificationPhoneNumber!}
        setIsMFASetupModalOpen={setIsMFASetupModalOpen}
        isForceMfa={isForceMfa}
      />
      {isAuthenticatorTypeMFA && (
        <AuthenticatorSetupForm
          isOpen={isAuthenticatorTypeMFA}
          onClose={() => setSelectedMFAMethod(undefined)}
          setIsMFASetupModalOpen={setIsMFASetupModalOpen}
          isForceMfa={isForceMfa}
        />
      )}
      {isDeleteMFAModalOpen && (
        <DeleteMFAModal
          isOpen={isDeleteMFAModalOpen}
          onClose={() => setIsDeleteMFAModalOpen(false)}
          keymakerCurrentUser={keymakerCurrentUser!}
        />
      )}
    </div>
  );
};
export default MFASettings;
